import { UpdateTimeEntriesParams } from "./api";
import { clone } from "./encoding";
import { Err, ErrorCode } from "./error";
import { classifyRemoval, markRemoved, prepareTimeEntriesForWrite } from "./time-entry-write";
import { throttle } from "./utils";
export const SYNC_EVENTS = {
    TIME_ENTRY_STATE_CHANGED: "TimeEntryStateChanged",
};
export class TimeEntrySyncService {
    constructor(api, state, throttleDelayMs = 1000) {
        this.api = api;
        this.state = state;
        this.throttleDelayMs = throttleDelayMs;
        this._activeSync = null;
        this._queuedSync = null;
        this._eventHandlers = new Map();
        this._eventHandlers.set(SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED, []);
        // Create throttled function with configurable delay
        this.throttledSyncTimeEntries = throttle(async () => {
            void this.syncTimeEntries();
        }, this.throttleDelayMs);
    }
    emit(event) {
        const handlers = this._eventHandlers.get(event) || [];
        handlers.forEach((handler) => handler());
    }
    on(event, handler) {
        const handlers = this._eventHandlers.get(event) || [];
        handlers.push(handler);
        this._eventHandlers.set(event, handlers);
    }
    off(event, handler) {
        const handlers = this._eventHandlers.get(event) || [];
        this._eventHandlers.set(event, handlers.filter((h) => h !== handler));
    }
    async createOrUpdateTimeEntries(entries, options = {}) {
        const { wait = false, publish = false, setUpdated = true } = options;
        entries = Array.isArray(entries) ? entries : [entries];
        for (const e of prepareTimeEntriesForWrite(entries, { publish, setUpdated })) {
            this.state.updatedTimeEntries.set(e.id, e);
        }
        if (wait) {
            await this.syncTimeEntries();
            // syncTimeEntries swallows FAILED_CONNECTION so that throttled
            // background syncs don't error-spam the user. But callers that
            // pass wait: true are interactive UIs awaiting confirmation —
            // silently leaving the entry queued lets the user retry and
            // produce duplicates. Surface the error and drop the queued copy.
            const stillQueued = entries.filter((e) => this.state.updatedTimeEntries.has(e.id));
            if (stillQueued.length !== 0) {
                for (const e of stillQueued) {
                    this.state.updatedTimeEntries.delete(e.id);
                }
                throw new Err(ErrorCode.FAILED_CONNECTION, "Keine Internetverbindung. Bitte versuchen Sie es erneut.");
            }
        }
        else {
            this.throttledSyncTimeEntries();
        }
        return entries;
    }
    async removeTimeEntries(entries, wait = false, publish = false) {
        entries = Array.isArray(entries) ? entries : [entries];
        entries.forEach((timeEntry) => {
            const removal = classifyRemoval(timeEntry);
            markRemoved(timeEntry, { publish });
            if (removal === "hard") {
                this.state.updatedTimeEntries.delete(timeEntry.id);
                this.state.removedTimeEntries.set(timeEntry.id, timeEntry);
            }
            else {
                this.state.updatedTimeEntries.set(timeEntry.id, timeEntry);
            }
        });
        if (wait) {
            await this.syncTimeEntries();
        }
        else {
            this.throttledSyncTimeEntries();
        }
    }
    async syncTimeEntries() {
        let queued = this._queuedSync;
        let active = this._activeSync;
        if (queued) {
            // There is already a queued sync promise, so just return that one
            return queued;
        }
        if (active) {
            // There is already a synchronization in process. wait for the current sync to finish
            // before starting a new one.
            const next = () => {
                this._queuedSync = null;
                return this.syncTimeEntries();
            };
            queued = active.then(next, next);
            this._queuedSync = queued;
            return queued;
        }
        active = this._syncTimeEntries().then(() => {
            this._activeSync = null;
        }, (error) => {
            this._activeSync = null;
            error.display = true;
            throw error;
        });
        this._activeSync = active;
        return active;
    }
    isUpdated(entry) {
        return this.state.updatedTimeEntries.has(entry.id);
    }
    isRemoved(entry) {
        return this.state.removedTimeEntries.has(entry.id);
    }
    clearSyncState() {
        this.state.updatedTimeEntries.clear();
        this.state.removedTimeEntries.clear();
        this.emit(SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED);
    }
    async _syncTimeEntries() {
        const updated = [...this.state.updatedTimeEntries.values()].map(clone);
        const removed = [...this.state.removedTimeEntries.values()];
        if (updated.length || removed.length) {
            try {
                await this.api.updateTimeEntries(new UpdateTimeEntriesParams({ updated, removed }));
                removed.forEach((e) => this.state.removedTimeEntries.delete(e.id));
                updated.forEach((entry) => {
                    const updatedEntry = this.state.updatedTimeEntries.get(entry.id);
                    if (updatedEntry && updatedEntry.updated <= entry.updated) {
                        this.state.updatedTimeEntries.delete(entry.id);
                    }
                });
                // Emit event to signal that state should be saved
                this.emit(SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED);
            }
            catch (e) {
                if (e.code !== ErrorCode.FAILED_CONNECTION) {
                    this.state.updatedTimeEntries.clear();
                    this.state.removedTimeEntries.clear();
                    // Emit event to signal that state should be saved
                    this.emit(SYNC_EVENTS.TIME_ENTRY_STATE_CHANGED);
                    throw e;
                }
            }
        }
    }
}
//# sourceMappingURL=time-entry-sync.service.js.map